/**
 * `<plp-field>` — универсальное поле формы.
 *
 * Тип конкретного поведения определяется атрибутом `type` (см. types/).
 * При изменении атрибутов name/type/label/placeholder/required/default/help
 * вызывается re-render через зарегистрированный type-плагин.
 *
 * Двусторонний sync `label` ↔ DOM: MutationObserver на дочернем
 * `<label.plp-field-label>` копирует textContent → атрибут `label`.
 */

import { getFieldType, KNOWN_TYPES } from './types/index.js';
import type { ValidationResult } from './validation.js';
import { showError } from './types/base.js';

const OBSERVED_ATTRS = [
  'name',
  'type',
  'label',
  'placeholder',
  'required',
  'default',
  'help',
  'crm',
  'error-message',
  'value',
  'link-url',
  'link-label',
  'country',
  'min',
  'max',
  'pattern',
  'minlength',
  'maxlength',
  'rows',
];

export class PlpField extends HTMLElement {
  static get observedAttributes(): string[] {
    return OBSERVED_ATTRS;
  }

  private _labelObserver?: MutationObserver;
  /** Защита от рекурсии при двустороннем sync label ↔ атрибут */
  private _applyingLabel = false;

  connectedCallback(): void {
    this.render();
    this.bindEvents();
    this.observeLabel();
  }

  disconnectedCallback(): void {
    this._labelObserver?.disconnect();
  }

  attributeChangedCallback(name: string, _old: string | null, _next: string | null): void {
    if (name === 'label' && this._applyingLabel) return;
    if (this.isConnected) this.render();
  }

  private getCtx() {
    return {
      name: this.getAttribute('name') ?? '',
      label: this.getAttribute('label') ?? undefined,
      placeholder: this.getAttribute('placeholder') ?? undefined,
      required: this.hasAttribute('required'),
      defaultValue: this.getAttribute('default') ?? undefined,
      help: this.getAttribute('help') ?? undefined,
      errorMessage: this.getAttribute('error-message') ?? undefined,
      host: this as HTMLElement,
    };
  }

  private getTypeKey(): string {
    const t = (this.getAttribute('type') ?? 'text').toLowerCase();
    return KNOWN_TYPES.includes(t) ? t : 'text';
  }

  render(): void {
    if (!this.getAttribute('name')) {
      // Без name тег ничего не делает — пишем подсказку только в edit-режиме
      return;
    }
    const type = getFieldType(this.getTypeKey());
    type.render(this.getCtx());
  }

  private bindEvents(): void {
    this.addEventListener('blur', this.onBlur, true);
    this.addEventListener('focus', this.onFocus, true);
  }

  private onBlur = (): void => {
    const r = this.validate();
    if (!r.valid) showError(this, r.error);
  };

  private onFocus = (): void => {
    showError(this, undefined);
  };

  private observeLabel(): void {
    this._labelObserver = new MutationObserver(() => {
      if (this._applyingLabel) return;
      const lbl = this.querySelector<HTMLLabelElement>(':scope > label.plp-field-label');
      if (!lbl) return;
      const text = lbl.firstChild?.textContent?.trim() ?? '';
      const current = this.getAttribute('label') ?? '';
      if (text !== current) {
        this._applyingLabel = true;
        this.setAttribute('label', text);
        this._applyingLabel = false;
      }
    });
    this._labelObserver.observe(this, {
      subtree: true,
      childList: true,
      characterData: true,
    });
  }

  /** Используется `<plp-form>` на submit и autoblur'е. */
  validate(): ValidationResult {
    const type = getFieldType(this.getTypeKey());
    return type.validate(this.getCtx());
  }

  /** Текущее значение поля для payload'а формы. */
  getValue(): string | string[] {
    const type = getFieldType(this.getTypeKey());
    return type.getValue(this.getCtx());
  }

  shouldSubmit(): boolean {
    const type = getFieldType(this.getTypeKey());
    return type.shouldSubmit(this.getCtx());
  }

  setDisabled(disabled: boolean): void {
    const type = getFieldType(this.getTypeKey());
    type.setDisabled(this.getCtx(), disabled);
    if (disabled) this.setAttribute('aria-disabled', 'true');
    else this.removeAttribute('aria-disabled');
  }

  showError(message?: string): void {
    showError(this, message);
  }
}
